import { createClient } from '@supabase/supabase-js'
import type { Database } from '../types/database.types'

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing Supabase environment variables. Check .env.local file.')
}

/**
 * With persistSession true and navigator.locks available, @supabase/auth-js
 * defaults to serializing every auth operation (initialize, getSession,
 * exchangeCodeForSession, refreshSession, ...) behind a real browser
 * Navigator LockManager lock, aborting with "signal is aborted without
 * reason" if that lock isn't granted within lockAcquireTimeout (10s
 * default) — see node_modules/@supabase/auth-js/src/lib/locks.ts,
 * navigatorLock(). In this deployment that lock request was never actually
 * being granted (confirmed via a console stack trace landing in
 * GoTrueClient's own initialize() -> _acquireLock -> navigatorLock, with no
 * corresponding network request ever attempted — a pure browser-API-level
 * stall, reproducing on every device/browser tested, including fresh
 * incognito windows). auth-js already falls back to a no-op lock (just runs
 * the operation directly, no cross-tab serialization) in environments
 * without navigator.locks support; this forces that same, SDK-sanctioned
 * fallback explicitly rather than keep depending on a browser API that
 * isn't working for this hosting environment.
 */
async function noOpLock<R>(_name: string, _acquireTimeout: number, fn: () => Promise<R>): Promise<R> {
  return await fn()
}

/**
 * Supabase client instance for FirmNest application.
 * Configured with database types for full type safety.
 */
export const supabase = createClient<Database>(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    // The auth callback pages (auth-callback-page.tsx, reset-password-page.tsx)
    // already call exchangeCodeForSession explicitly. With this left `true`,
    // the SDK's own automatic detection races that explicit call for the same
    // one-time-use code the instant the page loads. Disabling it makes the
    // page's own explicit exchange the only thing that ever consumes the code.
    detectSessionInUrl: false,
    flowType: 'pkce',
    lock: noOpLock
  }
})
